/**
 * Chart colors for the survey results, validated against the admin's dark surface
 * (#161E25) rather than chosen by eye.
 *
 * The palette used by the older dashboard graphs — #0B75C2 / #095B97 / #2C3A44 / #30363B —
 * does not survive a check: its last two slots sit at Delta-E 1.7 from each other for a
 * reader with *normal* colour vision, so on a four-slice donut two slices are effectively
 * the same colour. Rather than inherit that, the categorical ramp below keeps the brand
 * blue in slot 1 and takes the remaining hues from a set that passes on this surface:
 *
 *   lightness band PASS · chroma floor PASS · CVD separation PASS (worst adjacent
 *   Delta-E 8.4 protan) · normal-vision floor PASS (19.3) · contrast vs surface PASS (all >= 3:1)
 *
 * Slots are assigned in fixed order and never cycled — past 8 options a question renders
 * as bars instead, so a 9th slice never reuses slot 1's hue.
 */

/** Chart surface, for the gap between adjacent segments. */
export const CHART_SURFACE = "#161E25";

/** Categorical slots, in fixed assignment order. Brand blue first. */
export const SERIES_COLORS = [
  "#0B75C2", // brand blue
  "#D95926", // orange
  "#199E70", // aqua
  "#C98500", // yellow
  "#D55181", // magenta
  "#008300", // green
  "#9085E9", // violet
  "#E66767", // red
];

/** Above this many slices a donut stops being readable; render bars instead. */
export const MAX_DONUT_SLICES = SERIES_COLORS.length;

export function seriesColor(index: number): string {
  return SERIES_COLORS[index % SERIES_COLORS.length];
}

/**
 * Single hue for magnitude-only bars (multi-choice, suggestion usage, ratings), where the
 * category label sits next to the bar and colour carries no identity of its own.
 */
export const MAGNITUDE_COLOR = "#0B75C2";
export const TRACK_COLOR = "#222B33";

/**
 * NPS is a polarity read, so it gets a diverging pair with a neutral middle — red to brand
 * blue, not red to green: red/green fails CVD separation (Delta-E 4.1 deutan), while
 * red/blue clears it at 20.6. The three groups are always labelled in text as well, so the
 * split never rests on colour alone.
 */
export const NPS_COLORS = {
  detractor: "#D03B3B",
  passive: "#6B7A85",
  promoter: "#0B75C2",
};

export function npsGroupColor(score: number): string {
  if (score >= 9) return NPS_COLORS.promoter;
  if (score >= 7) return NPS_COLORS.passive;
  return NPS_COLORS.detractor;
}
